import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
// import ButtonComponent from "../../components/ButtonComponent";
// import MobileCodeDropDownComponent from "../../components/MobileCodeDropDownComponent";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { getCountries, verifyMobile, setMobileNumber, setOTPstatus, loginAPI, setCountryCode } from "../../redux/features/userAuth/userAuth";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toastComponent } from "../../components/ToastComponent";
import useCookies from "react-cookie/cjs/useCookies";
// import backArrow from "../../assets/icons/up arrow.svg";
import React from "react";
import DynamicLogo from "../../components/DynamicLogo";

type Props = {};

const PRIMARY = "#28588C";

const LoginPage = (props: Props) => {
  const [_cookie, setCookie] = useCookies(["authToken", "refreshToken", "type"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const OTPsent = useAppSelector((state) => state.userAuth.OTPSent);
  const [OTPvalue, setOTPvalue] = useState<string[]>(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState("");
  const countryDropdownRef = React.useRef<HTMLDivElement>(null);

  const setFormValue = (e: any) => {
    dispatch(setCountryCode({ countryCode: e.phonecode, id: e.id }));
  };

  const schema = yup.object().shape({
    mobile: yup.string().matches(/^\+?\d{10,14}$/, "Enter a Valid Mobile Number"),
  });

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    Object.values(errors).forEach((element: any) => {
      toastComponent(element.message, "error");
    });
  }, [errors]);

  const countriesFromStore = useAppSelector((state) => state.userAuth.countries);
  const country_code = useAppSelector((state) => state.userAuth.countryCode);

  // Guard: when country API or branding fails, store can have undefined/non-array – avoid .find/.filter on undefined
  const countries = Array.isArray(countriesFromStore) ? countriesFromStore : [];

  useEffect(() => {
    dispatch(getCountries());
  }, []);

  // Get selected country object
  const selectedCountry = React.useMemo(() => {
    return countries.find((country) => country?.phonecode === country_code);
  }, [countries, country_code]);

  // Filter countries based on search query
  const filteredCountries = React.useMemo(() => {
    if (!countrySearchQuery.trim()) return countries;
    return countries.filter(
      (country) =>
        country?.name?.toLowerCase().includes(countrySearchQuery.toLowerCase()) ||
        country?.phonecode?.toLowerCase().includes(countrySearchQuery.toLowerCase())
    );
  }, [countries, countrySearchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const mobileNumber = watch("mobile");

  const handleReset = () => {
    reset();
    setOTPvalue(["", "", "", "", "", ""]);
    dispatch(setOTPstatus(false));
  };

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  useEffect(() => {
    if (!id) {
      navigate("/login?id=");
    }
  }, [id]);

  // Resend timer effect
  useEffect(() => {
    if (resendTimer <= 0) return;
    const timerId = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(timerId);
  }, [resendTimer]);

  const sentOTP = () => {
    dispatch(setMobileNumber(mobileNumber));
    dispatch(verifyMobile({ mobile_number: mobileNumber, catalog_id: id, country_code: country_code })).then((res) => {
      if (res?.payload?.success === true) {
        toastComponent(res?.payload?.message, "success");
        setResendTimer(30); // Start resend timer
      } else {
        toastComponent(res?.payload?.message, "error");
      }
    });
  };

  const handleResendOTP = () => {
    if (resendTimer > 0) return;
    setResendTimer(30);
    sentOTP();
  };

  const otpHandle = () => {
    const otpString = OTPvalue.join("");
    if (otpString.length !== 6) {
      toastComponent("Please enter complete OTP", "error");
      return;
    }

    setSubmitting(true);
    dispatch(loginAPI({ mobile_number: mobileNumber, otp: otpString, catalog_id: id, country_code: country_code })).then((res) => {
      setSubmitting(false);
      if (res?.payload?.success === true && typeof res.payload.authToken === "string") {
        localStorage.setItem("name", res?.payload?.data?.full_name);
        localStorage.setItem("number", res?.payload?.data?.mobile_number);
        setCookie("authToken", res.payload.authToken, { path: "/" });
        setCookie("refreshToken", res.payload.refreshToken, { path: "/" });
        setCookie("type", 0, { path: "/" });
        toastComponent(res.payload.message, "success");
        navigate(`/catalogues/${id}`);
        dispatch(setOTPstatus(false));
      } else if (res.payload.is_register === false) {
        dispatch(setOTPstatus(false));
        navigate(`/register?id=${id}`);
      } else {
        dispatch(setOTPstatus(true));
        toastComponent("Incorrect OTP", "error");
      }
    });
  };

  useEffect(() => {
    if (mobileNumber === "") {
      dispatch(setOTPstatus(false));
    }
  }, [mobileNumber]);

  useEffect(() => {
    document.title = "JewelMaker | Login to Catalogue";
  }, []);

  // OTP input handlers
  const otpRefs = React.useRef<Array<HTMLInputElement | null>>([]);

  const setOtpAt = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...OTPvalue];
    newOtp[index] = digit || "";
    setOTPvalue(newOtp);
    if (digit && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !OTPvalue[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const phoneValid = /^\+?\d{10,14}$/.test(mobileNumber || "");
  const otpFilled = OTPvalue.join("").length === 6 && OTPvalue.every((d) => /\d/.test(d));
  const canLogin = phoneValid && otpFilled && !submitting;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 text-slate-900" style={{ ["--jm" as any]: PRIMARY }}>
      <div className="max-w-[980px] mx-auto px-4 py-10 grid place-items-center min-h-screen">
        <div className="w-full max-w-xl">
          {/* Logo */}
          <div className="flex items-center justify-center mb-6">
            <DynamicLogo alt="JewelMaker" className="w-[200px]" catalogId={id ?? undefined} />
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 md:p-8">
            {/* <button 
              type="button" 
              onClick={handleReset}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              ← Back
            </button> */}

            <h1 className="text-xl md:text-2xl font-semibold mt-2 text-dynamicColor-primary">Sign in to view catalogue</h1>

            <form className="mt-6 grid gap-5" onSubmit={!OTPsent ? handleSubmit(sentOTP) : handleSubmit(otpHandle)}>
              {/* Phone Input */}
              <label className="block text-sm">
                <span className="text-slate-700 font-medium">
                  Mobile Number <b className="text-red-500">*</b>
                </span>
                <div className="mt-1 flex flex-col sm:flex-row gap-2">
                  <div className="relative w-full sm:w-[220px]" ref={countryDropdownRef}>
                    <button
                      type="button"
                      onClick={() => !OTPsent && setCountryDropdownOpen(!countryDropdownOpen)}
                      disabled={OTPsent}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--jm)] flex items-center justify-between bg-white disabled:opacity-60"
                    >
                      <span className="truncate">
                        {selectedCountry ? `${selectedCountry.phonecode} (${selectedCountry.name})` : country_code}
                      </span>
                      <svg className={`w-4 h-4 transition-transform flex-shrink-0 ml-2 ${countryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {countryDropdownOpen && (
                      <div 
                        className="absolute z-50 mt-1 w-full sm:w-[300px] bg-white border border-slate-300 rounded-xl shadow-lg max-h-[300px] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-2 border-b border-slate-200">
                          <input
                            type="text"
                            placeholder="Search country..."
                            value={countrySearchQuery}
                            onChange={(e) => setCountrySearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[var(--jm)] focus:outline-none"
                            autoFocus
                          />
                        </div>
                        <div className="overflow-y-auto">
                          {filteredCountries && filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => (
                              <button
                                key={country.id}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFormValue(country);
                                  setCountryDropdownOpen(false);
                                  setCountrySearchQuery("");
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 focus:bg-slate-100 focus:outline-none"
                              >
                                {country.phonecode} ({country.name})
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-sm text-slate-500">No countries found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    {...register("mobile")}
                    value={mobileNumber || ""}
                    onChange={(e) => setValue("mobile", e.target.value.replace(/\D/g, ""))}
                    inputMode="numeric"
                    placeholder="Enter mobile number"
                    className="w-full flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--jm)]"
                    disabled={OTPsent}
                  />
                </div>
                <div className="text-xs mt-1 text-slate-500">We'll send a 6-digit OTP to this number.</div>
                {errors.mobile && <div className="text-xs text-red-600 mt-1">{errors.mobile.message as string}</div>}
              </label>

              {/* OTP Input */}
              {OTPsent && (
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-700 font-medium">
                      Enter OTP <b className="text-red-500">*</b>
                    </span>
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendTimer > 0}
                      className={`text-sm ${resendTimer > 0 ? "text-slate-400 cursor-not-allowed" : "text-dynamicColor-primary hover:underline"}`}
                    >
                      {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP"}
                    </button>
                  </div>

                  {/* OTP Input Row */}
                  <div className="mt-2 flex items-center gap-3">
                    {Array.from({ length: 6 })?.map((_, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        value={OTPvalue[index] || ""}
                        onChange={(e) => setOtpAt(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        inputMode="numeric"
                        className="w-12 h-12 text-center text-lg rounded-xl border border-slate-300 focus:ring-2 focus:ring-dynamicColor-primary"
                        aria-label={`OTP digit ${index + 1}`}
                        maxLength={1}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={OTPsent ? !canLogin : !phoneValid}
                className={`mt-2 w-full px-4 py-3 rounded-xl text-white font-medium ${(OTPsent ? canLogin : phoneValid) && !submitting ? "" : "opacity-60 cursor-not-allowed"} ${
                  OTPsent ? "background-secondary" : "background-primary"
                }`}
              >
                {submitting ? "Logging in…" : !OTPsent ? "Get OTP" : "Log in"}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-slate-500 mt-4">By continuing you agree to our Terms and Privacy Policy.</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
